import {
  RECOMMENDED_STEP_IDS,
  RECOMMENDED_DESCRIPTION_MAX_LENGTH,
  isRecommendedModelProfileWrite,
  normalizeRecommendedDescription,
  type RecommendedModelProfileView,
  type RecommendedModelProfileWrite,
  type RecommendedStepId,
} from '../../../../shared-types/src/recommended-models.js';
import type { RecommendedThinkingLevel } from '../../../../shared-types/src/model-thinking.js';

export interface RecommendedDraft {
  enabled: boolean;
  defaultStepId: '3';
  steps: Array<{
    id: RecommendedStepId;
    catalogModelId: string;
    thinkingLevel: RecommendedThinkingLevel | '';
    description: string;
  }>;
}
export function recommendedDraft(view?: RecommendedModelProfileView): RecommendedDraft {
  return view?.profile
    ? {
        enabled: view.profile.enabled,
        defaultStepId: '3',
        steps: view.profile.steps.map(step => ({ ...step, description: step.description ?? '' })),
      }
    : {
        enabled: false,
        defaultStepId: '3',
        steps: RECOMMENDED_STEP_IDS.map((id) => ({ id, catalogModelId: '', thinkingLevel: '', description: '' })),
      };
}
export function changeRecommendedModel(
  draft: RecommendedDraft,
  id: RecommendedStepId,
  modelId: string,
  view: RecommendedModelProfileView
): RecommendedDraft {
  const levels = view.candidates.find((c) => c.model.id === modelId)?.thinkingLevels ?? [];
  return {
    ...draft,
    steps: draft.steps.map((step) =>
      step.id !== id
        ? step
        : {
            ...step,
            catalogModelId: modelId,
            description: step.catalogModelId === modelId ? step.description : '',
            thinkingLevel:
              step.thinkingLevel && levels.includes(step.thinkingLevel) ? step.thinkingLevel : '',
          }
    ),
  };
}
export function recommendedDraftErrors(
  draft: RecommendedDraft,
  view: RecommendedModelProfileView
): string[][] {
  const disabling =
    view.profile &&
    !draft.enabled &&
    view.profile.steps.every((step, index) =>
      step.id === draft.steps[index]?.id &&
      step.catalogModelId === draft.steps[index]?.catalogModelId &&
      step.thinkingLevel === draft.steps[index]?.thinkingLevel);
  return draft.steps.map((step, index) => {
    const candidate = view.candidates.find((c) => c.model.id === step.catalogModelId);
    const previous = view.profile?.steps[index];
    const changedPair = previous && (previous.catalogModelId !== step.catalogModelId ||
      previous.thinkingLevel !== step.thinkingLevel);
    return [
      ...(normalizeRecommendedDescription(step.description).length > RECOMMENDED_DESCRIPTION_MAX_LENGTH
        ? ['descriptionTooLong'] : []),
      ...(!step.catalogModelId || !step.thinkingLevel ? ['required'] : []),
      ...(step.catalogModelId &&
      step.thinkingLevel &&
      draft.steps.filter(
        (s) => s.catalogModelId === step.catalogModelId && s.thinkingLevel === step.thinkingLevel
      ).length > 1
        ? ['duplicate']
        : []),
      ...(!disabling &&
      step.catalogModelId &&
      step.thinkingLevel &&
      (!candidate ||
        ((draft.enabled || changedPair) && (candidate.reasons.length ||
          !candidate.thinkingLevels.includes(step.thinkingLevel))))
        ? ['unavailable']
        : []),
    ];
  });
}
function comparableDraft(draft: RecommendedDraft): string {
  return JSON.stringify({
    enabled: draft.enabled,
    defaultStepId: draft.defaultStepId,
    steps: draft.steps.map(step => ({
      id: step.id,
      catalogModelId: step.catalogModelId,
      thinkingLevel: step.thinkingLevel,
      description: normalizeRecommendedDescription(step.description),
    })),
  });
}
export function canSaveRecommendedDraft(
  draft: RecommendedDraft,
  view: RecommendedModelProfileView
): draft is RecommendedDraft & RecommendedModelProfileWrite {
  return (
    isRecommendedModelProfileWrite(draft) &&
    recommendedDraftErrors(draft, view).every((errors) => errors.length === 0)
  );
}
export async function saveRecommendedDraft(
  draft: RecommendedModelProfileWrite,
  put: () => Promise<RecommendedModelProfileView>,
  get: () => Promise<RecommendedModelProfileView>
): Promise<{ kind: 'saved' | 'conflict'; view: RecommendedModelProfileView }> {
  try {
    return { kind: 'saved', view: await put() };
  } catch (error) {
    let current: RecommendedModelProfileView;
    try {
      current = await get();
    } catch {
      throw error;
    }
    if (comparableDraft(recommendedDraft(current)) === comparableDraft({
      ...draft, steps: draft.steps.map(step => ({ ...step, description: step.description ?? '' })),
    }))
      return { kind: 'saved', view: current };
    if ((error as { status?: number } | undefined)?.status === 409)
      return { kind: 'conflict', view: current };
    throw error;
  }
}
